import { createContext, useContext, useSyncExternalStore } from 'react';

export type FormulaTraceTargetKind = 'tab' | 'block' | 'row' | 'column' | 'field' | 'table' | 'tableColumn';
export type FormulaTraceRuleKind =
    'visibility' | 'required' | 'readonly' | 'formula' | 'style' | 'min' | 'max' | 'workCalendar';
export type FormulaTraceStatus = 'evaluated' | 'fallback' | 'error';

export interface IFormulaTraceTarget {
    targetId: string;
    targetKind: FormulaTraceTargetKind;
    targetName?: string;
    ruleKind: FormulaTraceRuleKind;
    parentTargetId?: string;
    parentTargetKind?: FormulaTraceTargetKind;
}

export interface IFormulaTrace extends IFormulaTraceTarget {
    id: string;
    source: string;
    preparedExpression?: string;
    result: unknown;
    dependencies: Array<{ field: string; value: unknown }>;
    externalSources: string[];
    status: FormulaTraceStatus;
    error?: string;
    evaluatedAt: number;
}

export const getFormulaTraceId = (target: IFormulaTraceTarget): string =>
    `${target.targetKind}:${target.targetId}:${target.ruleKind}`;

// DOM-метки target'а для навигации «К элементу» (formulaTraceNavigation).
// Единственное место, где живут имена data-атрибутов и guard пустого id:
// без id не ставим ни один из атрибутов, иначе селектор находит «пустые» узлы.
export const formulaTraceAttrs = (
    targetId: string | undefined,
    targetKind: FormulaTraceTargetKind,
): Record<string, string | undefined> =>
    targetId
        ? { 'data-formula-trace-target-id': targetId, 'data-formula-trace-target-kind': targetKind }
        : {};

const emptySnapshot: readonly IFormulaTrace[] = [];

export class DocumentFormulaTraceRegistry {
    private readonly traces = new Map<string, IFormulaTrace>();
    private readonly listeners = new Set<() => void>();
    private snapshot: readonly IFormulaTrace[] = emptySnapshot;
    private snapshotDirty = false;
    private notificationScheduled = false;

    constructor(
        readonly documentId?: string,
        readonly sourceScheme?: unknown,
    ) {}

    readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    // Снапшот пересобирается лениво: живая карточка пишет trace при каждом
    // вычислении формулы, а читатель появляется только с открытым Workbench.
    readonly getSnapshot = (): readonly IFormulaTrace[] => {
        if (this.snapshotDirty) {
            this.snapshotDirty = false;
            this.snapshot = Array.from(this.traces.values()).sort(
                (left, right) =>
                    left.targetKind.localeCompare(right.targetKind) ||
                    (left.targetName || left.targetId).localeCompare(right.targetName || right.targetId) ||
                    left.ruleKind.localeCompare(right.ruleKind),
            );
        }
        return this.snapshot;
    };

    upsert(trace: Omit<IFormulaTrace, 'id' | 'evaluatedAt'>): void {
        const id = getFormulaTraceId(trace);
        this.traces.set(id, { ...trace, id, evaluatedAt: Date.now() });
        this.markChanged();
    }

    remove(target: IFormulaTraceTarget): void {
        if (!this.traces.delete(getFormulaTraceId(target))) return;
        this.markChanged();
    }

    clear(): void {
        if (this.traces.size === 0) return;
        this.traces.clear();
        this.markChanged();
    }

    private markChanged(): void {
        this.snapshotDirty = true;
        if (this.listeners.size === 0 || this.notificationScheduled) return;

        this.notificationScheduled = true;
        queueMicrotask(() => {
            this.notificationScheduled = false;
            this.listeners.forEach((listener) => listener());
        });
    }
}

export const DocumentFormulaTraceContext = createContext<DocumentFormulaTraceRegistry | undefined>(undefined);
export const DocumentFormulaTraceParentContext = createContext<
    Pick<IFormulaTraceTarget, 'targetId' | 'targetKind'> | undefined
>(undefined);

const emptySubscribe = () => () => undefined;
const getEmptySnapshot = () => emptySnapshot;

export const useDocumentFormulaTraceRegistry = (): DocumentFormulaTraceRegistry | undefined =>
    useContext(DocumentFormulaTraceContext);

export const useDocumentFormulaTraces = (): readonly IFormulaTrace[] => {
    const registry = useDocumentFormulaTraceRegistry();
    return useSyncExternalStore(
        registry?.subscribe ?? emptySubscribe,
        registry?.getSnapshot ?? getEmptySnapshot,
        registry?.getSnapshot ?? getEmptySnapshot,
    );
};
